import moment, {Moment} from 'moment';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Environment} from 'notebook/tables/excess/Evaluator';
import {evalFunction} from 'notebook/tables/excess/FunctionEval';
import {getFunction} from 'notebook/tables/excess/functions';
import * as util from 'notebook/tables/excess/Util';
import {
  BaseTypedValueType,
  CellIdWithOptionalTable,
  TABLE_MAX_COLUMNS,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {coerceToSingleValue} from 'notebook/tables/typed-values/DataTypes';
import {NumberTypedValue, ScientificTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {Optional} from 'util/Types';

export function getPrecisionIfScientific(astValues: Array<Optional<TypedValue>>): Optional<number> {
  const precisions: Array<number> = [];
  if (astValues.length === 0) {
    return null;
  }
  const firstValue = astValues[0];
  if (firstValue == null || firstValue.type !== util.VALUE_TYPES.SCIENTIFIC) {
    return null;
  }
  for (const value of astValues) {
    if (value != null && value.type === util.VALUE_TYPES.SCIENTIFIC) {
      const defaultPrecision = value.defaultPrecision;
      if (defaultPrecision != null) {
        precisions.push(defaultPrecision);
      }
    }
  }
  if (precisions.length === 0) {
    return null;
  }
  return Math.max(...precisions);
}

/**
 * @returns whether `value` is a number or a date(time) that can be converted into a number.
 */
function isNumericLike(value: number | string | Moment | null): value is number | Moment {
  return typeof value === 'number' || moment.isMoment(value);
}

/**
 * @returns value if value is a number, or the number of days since epoch if value is a Moment.
 * @throws if value is not a number or a Moment.
 */
function convertToNumber(value: number | Moment): number {
  assert(isNumericLike(value), 'Expected value to be a number or moment');
  return moment.isMoment(value) ? util.momentToDaysSinceEpoch(value) : value;
}

export abstract class ASTNode {
  eval(env: Environment): TypedValue {
    // Given an environment, returns the computed value of a cell
    //   For now, this is probably a number, could maybe be a string.
    // Should throw an error when sub-expressions are NaN / Infinity ?
    // Implemented by subclasses.
    throw new Error('eval not implemented');
  }

  // This is a mechanism for a node to modify its children.
  // It is currently used for LOOKUPFIELD function to be able to add dependency on nodes in addition to the
  // ones that are specified as function arguments. We don't expect this method to be implemented for
  // most of the node types.
  prepareChildren(rowIndex: number, columnDefinitions: Array<ColumnDefinition | null> | null): void {
    for (const child of this.childNodes()) {
      child.prepareChildren(rowIndex, columnDefinitions);
    }
  }

  childNodes(): Array<ASTNode> {
    return [];
  }
  /**
   * Enumerates the cells that this node needs in order to complete evaluation.
   * When env is present, node may optimize enumeration accordingly.
   * @param env The environment the node exists within (should contain fullTableMap and currentTable)
   */
  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    // A list of cell ids that this node's value depends on.
    // Implemented by subclasses.
    throw new Error('dependencies not implemented');
  }
  isSingular(): boolean {
    return true;
  }
  isTerminal(): boolean {
    // Returns if this subtree is a valid leaf in a parse tree.
    throw new Error('isTerminal is not implemented');
  }

  abstract equals(other: Optional<ASTNode>): boolean;
}

export class ReferenceErrorNode extends ASTNode {
  text: string = '#REF!';

  eval(env: Environment): TypedValue {
    throw new util.ReferenceError('Invalid reference');
  }
  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    return new Set();
  }
  isTerminal(): boolean {
    return true;
  }
  equals(other: Optional<ASTNode>): boolean {
    return other instanceof ReferenceErrorNode;
  }
}

export enum UnaryOperatorType {
  PLUS = '+',
  NEGATION = '-',
}

export class UnaryOperator extends ASTNode {
  type: UnaryOperatorType;
  child: ASTNode;

  constructor({type, child}: {type: UnaryOperatorType; child: ASTNode}) {
    super();
    this.type = type;
    this.child = child;
  }

  childNodes(): Array<ASTNode> {
    return [this.child];
  }
  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    return this.child.dependencies(env);
  }
  equals(other: Optional<ASTNode>): boolean {
    if (!(other instanceof UnaryOperator)) {
      return false;
    }
    if (this.type !== other.type) {
      return false;
    }
    return this.child.equals(other.child);
  }

  eval(env: Environment): TypedValue {
    const singleValue = coerceToSingleValue(this.child.eval(env));
    const childRawVal = singleValue.rawValue;
    const type = singleValue.type;
    // eslint-disable-next-line benchlint/no-in-operator
    const defaultPrecision = 'defaultPrecision' in singleValue ? singleValue.defaultPrecision : null;
    if (!isNumericLike(childRawVal)) {
      throw new util.ValueError(`Operator ${this.type} does not support '${childRawVal}'.`);
    }
    switch (this.type) {
      case UnaryOperatorType.PLUS:
        if (moment.isMoment(childRawVal)) {
          const value = util.daysSinceEpochToDatetimeString(convertToNumber(childRawVal));
          return util.astValue({
            value,
            type: BaseTypedValueType.DATETIME,
          });
        }
        // TODO(TypeScript): remove "as any" in favor of ensuring type in [NUMBER, SCIENTIFIC, DATETIME]
        assertAndContinue(type === 'NUMBER' || type === 'SCIENTIFIC', 'Expected number or scientific type', {
          type,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return util.astValue({rawValue: childRawVal, type, defaultPrecision} as any);
      case UnaryOperatorType.NEGATION:
        if (moment.isMoment(childRawVal)) {
          const value = util.daysSinceEpochToDatetimeString(-convertToNumber(childRawVal));
          return util.astValue({
            value,
            type: BaseTypedValueType.DATETIME,
          });
        }
        assertAndContinue(type === 'NUMBER' || type === 'SCIENTIFIC', 'Expected number or scientific type', {
          type,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return util.astValue({rawValue: -childRawVal, type, defaultPrecision} as any);
      default:
        throw new Error(`Unexpected type found for unary operator: ${this.type}`);
    }
  }

  isTerminal(): boolean {
    return this.child != null;
  }
}

export enum BinaryOperatorType {
  ADDITION = '+',
  SUBTRACTION = '-',
  MULTIPLICATION = '*',
  DIVISION = '/',
  EXPONENTIAL = '^',
  EQUALS = '=',
  NOT_EQUALS = '<>',
  LESS_THAN = '<',
  GREATER_THAN = '>',
  LESS_THAN_OR_EQUAL = '<=',
  GREATER_THAN_OR_EQUAL = '>=',
}

export class BinaryOperator extends ASTNode {
  type: BinaryOperatorType;
  left?: ASTNode;
  right?: ASTNode;

  // TODO(TypeScript): left and right should not be optional: BinaryOperator instantiation should not
  // happen in tokenizer.js (before left and right are known) but instead in parser.js.
  constructor({type, left, right}: {type: BinaryOperatorType; left?: ASTNode; right?: ASTNode}) {
    super();
    this.type = type;
    this.left = left;
    this.right = right;
  }

  _getLeftAndRight(): {left: ASTNode; right: ASTNode} {
    assert(Boolean(this.left && this.right), 'Expected left and right to be defined on BinaryOperator');
    return {
      left: this.left!,
      right: this.right!,
    };
  }

  childNodes(): Array<ASTNode> {
    const {left, right} = this._getLeftAndRight();
    return [left, right];
  }

  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    const {left, right} = this._getLeftAndRight();
    return new Set([...left.dependencies(env), ...right.dependencies(env)]);
  }

  equals(other: Optional<ASTNode>): boolean {
    if (!(other instanceof BinaryOperator)) {
      return false;
    }
    if (this.type !== other.type) {
      return false;
    }
    const leftEqual = this.left != null ? this.left.equals(other.left) : other.left == null;
    const rightEqual = this.right != null ? this.right.equals(other.right) : other.right == null;
    return leftEqual && rightEqual;
  }

  eval(env: Environment): TypedValue {
    const {left, right} = this._getLeftAndRight();
    const leftASTValue = coerceToSingleValue(left.eval(env));
    const rightASTValue = coerceToSingleValue(right.eval(env));
    let {rawValue: leftValue} = leftASTValue;
    let {rawValue: rightValue} = rightASTValue;

    if (
      [
        BinaryOperatorType.LESS_THAN_OR_EQUAL,
        BinaryOperatorType.LESS_THAN,
        BinaryOperatorType.EQUALS,
        BinaryOperatorType.NOT_EQUALS,
        BinaryOperatorType.GREATER_THAN_OR_EQUAL,
        BinaryOperatorType.GREATER_THAN,
      ].includes(this.type)
    ) {
      const booleanValue = this._booleanEval(leftValue, rightValue);
      return util.astValue({
        type: BaseTypedValueType.BOOLEAN,
        booleanValue,
      });
    }

    if (!isNumericLike(leftValue) && leftValue != null) {
      throw new util.ValueError(`Operator ${this.type} does not support '${leftValue}'.`);
    }
    if (!isNumericLike(rightValue) && rightValue != null) {
      throw new util.ValueError(`Operator ${this.type} does not support '${rightValue}'.`);
    }

    leftValue ??= 0;
    rightValue ??= 0;

    if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
      // If both operands are dates, return a number in days
      // We handle subtraction specially because it is likely to introduce floating point errors
      const rawValue =
        this.type === BinaryOperatorType.SUBTRACTION
          ? util.momentToDaysSince(leftValue, rightValue)
          : this._numericEval(
              util.momentToDaysSinceEpoch(leftValue),
              util.momentToDaysSinceEpoch(rightValue)
            );
      return util.astValue({rawValue, type: BaseTypedValueType.NUMBER});
    } else if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      // If both operands are numbers, do normal arithmetic
      const rawValue = this._numericEval(leftValue, rightValue);
      return util.astValue({
        rawValue,
        type: leftASTValue.type,
        defaultPrecision: getPrecisionIfScientific([
          leftASTValue as ScientificTypedValue | NumberTypedValue,
          rightASTValue as ScientificTypedValue | NumberTypedValue,
        ]),
      } as NumberTypedValue | ScientificTypedValue);
    } else {
      // If one operand is a date and the other is a number, return a date
      const leftNumber = convertToNumber(leftValue);
      const rightNumber = convertToNumber(rightValue);
      const value = util.daysSinceEpochToDatetimeString(this._numericEval(leftNumber, rightNumber));
      return util.astValue({
        value,
        type: BaseTypedValueType.DATETIME,
      });
    }
  }

  _numericEval(leftValue: number, rightValue: number): number {
    switch (this.type) {
      case BinaryOperatorType.ADDITION:
        return leftValue + rightValue;
      case BinaryOperatorType.SUBTRACTION:
        return leftValue - rightValue;
      case BinaryOperatorType.MULTIPLICATION:
        return leftValue * rightValue;
      case BinaryOperatorType.DIVISION:
        return this._divide(leftValue, rightValue);
      case BinaryOperatorType.EXPONENTIAL:
        return Math.pow(leftValue, rightValue);
      default:
        throw new Error(`Unexpected operator: ${this.type}`);
    }
  }

  _booleanEval(
    leftValue: number | string | Moment | null,
    rightValue: number | string | Moment | null
  ): boolean {
    switch (this.type) {
      case BinaryOperatorType.EQUALS:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) === util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isSame(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue) === convertToNumber(rightValue);
        } else {
          return (leftValue ?? '') === (rightValue ?? '');
        }
      case BinaryOperatorType.NOT_EQUALS:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) !== util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return !leftValue.isSame(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue) !== convertToNumber(rightValue);
        } else {
          return (leftValue ?? '') !== (rightValue ?? '');
        }
      case BinaryOperatorType.GREATER_THAN:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return leftValue > rightValue;
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isAfter(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue) > convertToNumber(rightValue);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return false;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return true;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return false;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (rightValue === null) {
          return true;
        } else {
          return false;
        }
      case BinaryOperatorType.GREATER_THAN_OR_EQUAL:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) >= util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isSameOrAfter(rightValue) || leftValue === rightValue;
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue) >= convertToNumber(rightValue);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return false;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return true;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return true;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (rightValue === null) {
          return true;
        } else {
          return false;
        }
      case BinaryOperatorType.LESS_THAN:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return leftValue < rightValue;
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isBefore(rightValue);
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue) < convertToNumber(rightValue);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return true;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return false;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return false;
        } else {
          return false;
        }
      case BinaryOperatorType.LESS_THAN_OR_EQUAL:
        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          // handle floating point equality correctly
          return util.capMaxFloatDecimalDigits(leftValue) <= util.capMaxFloatDecimalDigits(rightValue);
        } else if (moment.isMoment(leftValue) && moment.isMoment(rightValue)) {
          return leftValue.isSameOrBefore(rightValue) || leftValue === rightValue;
        } else if (isNumericLike(leftValue) && isNumericLike(rightValue)) {
          return convertToNumber(leftValue) <= convertToNumber(rightValue);
        } else if (typeof leftValue === 'number' && typeof rightValue === 'string') {
          return true;
        } else if (typeof leftValue === 'string' && typeof rightValue === 'number') {
          return false;
        }
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null && rightValue === null) {
          return true;
        } // eslint-disable-next-line benchlint/no-eqeqeq-null
        else if (leftValue === null) {
          return true;
        } else {
          return false;
        }
      default:
        throw new Error(`Unexpected operator: ${this.type}`);
    }
  }

  _divide(left: number, right: number): number {
    if (right === 0) {
      throw new util.DivZeroError('Divisor cannot be 0');
    } else {
      return left / right;
    }
  }

  isTerminal(): boolean {
    return this.left != null && this.right != null;
  }
}

export class FunctionOperator extends ASTNode {
  type: string;
  children: Array<ASTNode>;

  constructor({type, children}: {type: string; children: Array<ASTNode>}) {
    super();
    this.type = type;
    this.children = children;
  }

  // This is a mechanism for a function to add dependency on nodes in addition to the
  // ones that are specified as function arguments. A function may add extra function arguments based on
  // column definitions. An example is that LOOKUPFIELD function may add dependency on the STAGE_RUN cell.
  prepareChildren(rowIndex: number, columnDefinitions: Array<ColumnDefinition | null> | null): void {
    super.prepareChildren(rowIndex, columnDefinitions);
    const fn = getFunction(this.type);
    if (fn != null && fn.getExtraChildren != null) {
      this.children = [...this.children, ...fn.getExtraChildren({rowIndex, columnDefinitions, node: this})];
    }
  }

  childNodes(): Array<ASTNode> {
    return this.children;
  }
  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    const deps = new Set<CellIdWithOptionalTable>();
    for (const child of this.children) {
      for (const dep of child.dependencies(env)) {
        deps.add(dep);
      }
    }
    return deps;
  }
  equals(other: Optional<ASTNode>): boolean {
    if (!(other instanceof FunctionOperator)) {
      return false;
    }
    if (this.type !== other.type) {
      return false;
    }
    if (this.children.length !== other.children.length) {
      return false;
    }
    for (let i = 0; i < this.children.length; i++) {
      if (!this.children[i].equals(other.children[i])) {
        return false;
      }
    }
    return true;
  }

  eval(env: Environment): TypedValue {
    return evalFunction(this, env);
  }

  isTerminal(): boolean {
    return this.children != null;
  }
}

export interface CellLock {
  row: boolean;
  col: boolean;
}

export interface RangeLock {
  startRow: boolean;
  startCol: boolean;
  endRow: boolean;
  endCol: boolean;
}

export abstract class AbsoluteCellReference extends ASTNode {
  abstract text: string;

  getTable(): string | undefined | null {
    throw new Error('Not implemented');
  }

  static _relativeIdFor(
    cellId: CellIdWithOptionalTable,
    {
      anchorId,
      locks,
      includeTable,
    }: {anchorId: CellIdWithOptionalTable; locks: CellLock; includeTable: boolean}
  ): {cellId: CellIdWithOptionalTable; text: string} {
    let columnText;
    let relCol;
    let relRow;
    let rowText;
    const {row, column, table} = cellId;
    if (locks.row) {
      relRow = row;
      rowText = `R${relRow}`;
    } else {
      relRow = row - anchorId.row;
      rowText = `R[${relRow}]`;
    }

    if (locks.col) {
      relCol = column;
      columnText = `C${relCol}`;
    } else {
      relCol = column - anchorId.column;
      columnText = `C[${relCol}]`;
    }

    const tablePrefix = table != null && includeTable ? util.getTablePrefix(table) : '';
    return {
      cellId: {row: relRow, column: relCol, table},
      text: `${tablePrefix}${rowText}${columnText}`,
    };
  }

  toRelativeNode(anchorId: CellIdWithOptionalTable): RelativeCellReference {
    throw new Error('toRelativeNode not implemented');
  }
}

export class Cell extends AbsoluteCellReference {
  _dependencies: Set<CellIdWithOptionalTable>;
  id: CellIdWithOptionalTable;
  text: string;
  locks: CellLock;

  getTable(): string | undefined | null {
    return this.id.table;
  }

  eval(env: Environment): TypedValue {
    if (!this._validRef()) {
      throw new util.NameError(`Unknown range name: '${this.text}'`);
    }
    return env.get(this.id);
  }

  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    return this._dependencies;
  }

  toRelativeNode(anchorId: CellIdWithOptionalTable): RelCell {
    const {cellId, text} = Cell._relativeIdFor(this.id, {
      anchorId,
      locks: this.locks,
      includeTable: true,
    });
    return new RelCell({
      text,
      id: cellId,
      locks: _.clone(this.locks),
    });
  }

  constructor({id, text, locks}: {id: CellIdWithOptionalTable; text: string; locks: CellLock}) {
    super();
    this.id = id;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      row: false,
      col: false,
    });
    if (this._validRef()) {
      this._dependencies = new Set([this.id]);
    } else {
      this._dependencies = new Set();
    }
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof Cell &&
      isEqual(this.id, other.id) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }

  isTerminal(): boolean {
    return true;
  }

  _validRef(): boolean {
    return this.id.column < TABLE_MAX_COLUMNS;
  }
}

export class CellRange extends AbsoluteCellReference {
  _dependencies?: Set<CellIdWithOptionalTable>;
  start: CellIdWithOptionalTable;
  end: CellIdWithOptionalTable;
  locks: RangeLock;
  text: string;
  table?: string | null;

  getTable(): string | undefined | null {
    return this.start.table;
  }

  eval(env: Environment): TypedValue {
    if (!this._validRefs()) {
      throw new util.NameError(`Unknown range name: '${this.text}'`);
    }

    const {
      startRow: trimmedStartRow,
      startColumn: trimmedStartColumn,
      endRow: trimmedEndRow,
      endColumn: trimmedEndCol,
    } = this._calcTrimmedRange(env);

    const startRow = Math.min(trimmedStartRow, trimmedEndRow);
    const endRow = Math.max(trimmedStartRow, trimmedEndRow);
    const startCol = Math.min(trimmedStartColumn, trimmedEndCol);
    const endCol = Math.max(trimmedStartColumn, trimmedEndCol);

    // Returns a 2D array
    return util.astValue({
      type: BaseTypedValueType.ARRAY,
      rawValue: _.range(startRow, endRow + 1).map((row: number) =>
        util.astValue({
          type: BaseTypedValueType.ARRAY,
          rawValue: _.range(startCol, endCol + 1).map((column: number) =>
            env.get({row, column, table: this.table})
          ),
        })
      ),
    });
  }

  /**
   * Takes dimensions that are either asc or desc and trims them with the assumption that [0,maxIndex] are
   * locations of valid cells.
   *
   * Returns trimmed start and end in asc order if either startIndex or endIndex was less than or equal to
   * maxIndex. Returns maxIndex + 1 for both start and end if both startIndex and endIndex wer greater than
   * maxIndex.
   *
   * This is necessary because there is no concept of a zero size range in AST. If trimming the range would
   * in theory result in no valid cells, we must make the range point to an empty cell outside of the valid
   * cells.
   *
   * @param startIndex the user inputted starting index of the range
   * @param endIndex the user inputted ending index of the range
   * @param maxIndex the maximum index included in the table (inclusive)
   * @returns start and end such that start <= end <= (maxIndex + 1)
   */
  _trimDimensionRange(startIndex: number, endIndex: number, maxIndex: number): {start: number; end: number} {
    let start;
    let end;
    if (startIndex > maxIndex && endIndex > maxIndex) {
      start = maxIndex + 1;
      end = maxIndex + 1;
    } else {
      start = Math.min(startIndex, maxIndex);
      end = Math.min(endIndex, maxIndex);
    }
    return {start, end};
  }

  /**
   * Calculates the trimmed range based on the number of rows and max number of columns in
   * the referenced table. This is to prevent extra work from being performed if the user enters range
   * like A0:Z99 on a Table with a single cell.
   *
   * If not enough information to determine the optimal dimensions is given in the env,
   * returns the current range.
   *
   * Note: also trims the start row and column since ranges can be in ascending or descending order.
   *
   * @param env the environment containing FullTableMap and the name of the currentTable being processed
   * @returns dict containing the trimmed startRow, startColumn, endRow, and endColumn
   */
  _calcTrimmedRange(env?: Environment): {
    startRow: number;
    startColumn: number;
    endRow: number;
    endColumn: number;
  } {
    if (env) {
      const tableName = this.end.table ?? env.currentTable;
      if (tableName) {
        const table = env.fullTableMap[tableName];

        if (table != null && table.table != null) {
          const {start: startRow, end: endRow} = this._trimDimensionRange(
            this.start.row,
            this.end.row,
            table.table.length - 1
          );

          const maxWidth = Math.max(...table.table.map((row) => row.length));
          const {start: startColumn, end: endColumn} = this._trimDimensionRange(
            this.start.column,
            this.end.column,
            maxWidth - 1
          );

          return {startRow, startColumn, endRow, endColumn};
        }
      }
    }
    return {
      startRow: this.start.row,
      startColumn: this.start.column,
      endRow: this.end.row,
      endColumn: this.end.column,
    };
  }

  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    if (this._dependencies != null) {
      return this._dependencies;
    }
    this._dependencies = new Set();

    const {startRow, startColumn, endRow, endColumn} = this._calcTrimmedRange(env);

    if (this._validRefs()) {
      for (
        let row = startRow, end = endRow, asc = startRow <= end;
        asc ? row <= end : row >= end;
        asc ? row++ : row--
      ) {
        for (
          let column = startColumn, end1 = endColumn, asc1 = startColumn <= end1;
          asc1 ? column <= end1 : column >= end1;
          asc1 ? column++ : column--
        ) {
          this._dependencies.add({row, column, table: this.table});
        }
      }
    }
    return this._dependencies;
  }

  isTerminal(): boolean {
    return false;
  }

  toRelativeNode(anchorId: CellIdWithOptionalTable): RelCellRange {
    const {cellId: start, text: startText} = CellRange._relativeIdFor(this.start, {
      anchorId,
      locks: {row: this.locks.startRow, col: this.locks.startCol},
      includeTable: true,
    });
    const {cellId: end, text: endText} = CellRange._relativeIdFor(this.end, {
      anchorId,
      locks: {row: this.locks.endRow, col: this.locks.endCol},
      includeTable: false, // start cell includes the table.
    });
    return new RelCellRange({
      text: `${startText}:${endText}`,
      start,
      end,
      locks: _.clone(this.locks),
    });
  }

  constructor({
    start,
    end,
    text,
    locks,
  }: {
    start: CellIdWithOptionalTable;
    end: CellIdWithOptionalTable;
    text: string;
    locks?: Partial<RangeLock>;
  }) {
    super();
    this.start = start;
    this.end = end;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      startRow: false,
      startCol: false,
      endRow: false,
      endCol: false,
    });
    if (this.start.table !== this.end.table) {
      throw new util.FormulaParseError('Formula parse error');
    }
    this.table = this.start.table;
  }

  isSingular(): boolean {
    return false;
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof CellRange &&
      isEqual(this.start, other.start) &&
      isEqual(this.end, other.end) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }

  _validRefs(): boolean {
    return this.start.column < TABLE_MAX_COLUMNS && this.end.column < TABLE_MAX_COLUMNS;
  }
}

export abstract class RelativeCellReference extends ASTNode {
  static _absoluteIdFor(
    cellId: CellIdWithOptionalTable,
    {
      anchorId,
      locks,
      includeTable,
    }: {anchorId: CellIdWithOptionalTable; locks: CellLock; includeTable: boolean}
  ): {cellId: CellIdWithOptionalTable; text: string} {
    let absCol;
    let absRow;
    const {row, column, table} = cellId;
    if (locks.row) {
      absRow = row;
    } else {
      absRow = anchorId.row + row;
    }

    if (locks.col) {
      absCol = column;
    } else {
      absCol = anchorId.column + column;
    }

    return {
      cellId: {row: absRow, column: absCol, table},
      text: util.toCellReferenceTokenText(
        {row: absRow, column: absCol, table: includeTable ? table : undefined},
        locks
      ),
    };
  }

  toAbsoluteNode(anchorId: CellIdWithOptionalTable): AbsoluteCellReference | ReferenceErrorNode {
    throw new Error('toAbsoluteNode not implemented');
  }

  abstract text: string;
}

export class RelCell extends RelativeCellReference {
  id: CellIdWithOptionalTable;
  text: string;
  locks: CellLock;

  constructor({id, text, locks}: {id: CellIdWithOptionalTable; text: string; locks: Partial<CellLock>}) {
    super();
    this.id = id;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      row: false,
      col: false,
    });
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof RelCell &&
      isEqual(this.id, other.id) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }

  toAbsoluteNode(anchorId: CellIdWithOptionalTable): Cell | ReferenceErrorNode {
    const {cellId, text} = RelCell._absoluteIdFor(this.id, {
      anchorId,
      locks: this.locks,
      includeTable: true,
    });
    if (cellId.row < 0 || cellId.column < 0) {
      return new ReferenceErrorNode();
    }
    return new Cell({
      text,
      id: cellId,
      locks: _.clone(this.locks),
    });
  }
}

export class RelCellRange extends RelativeCellReference {
  locks: RangeLock;
  start: CellIdWithOptionalTable;
  end: CellIdWithOptionalTable;
  text: string;

  constructor({
    start,
    end,
    text,
    locks,
  }: {
    start: CellIdWithOptionalTable;
    end: CellIdWithOptionalTable;
    text: string;
    locks?: Partial<RangeLock>;
  }) {
    super();
    this.start = start;
    this.end = end;
    this.text = text;
    this.locks = _.defaults(locks || {}, {
      startRow: false,
      startCol: false,
      endRow: false,
      endCol: false,
    });
  }

  toAbsoluteNode(anchorId: CellIdWithOptionalTable): CellRange | ReferenceErrorNode {
    const {cellId: start, text: startText} = RelCell._absoluteIdFor(this.start, {
      anchorId,
      locks: {row: this.locks.startRow, col: this.locks.startCol},
      includeTable: true,
    });
    const {cellId: end, text: endText} = RelCell._absoluteIdFor(this.end, {
      anchorId,
      locks: {row: this.locks.endRow, col: this.locks.endCol},
      includeTable: false,
    });
    if (start.row < 0 || start.column < 0 || end.row < 0 || end.column < 0) {
      return new ReferenceErrorNode();
    }

    return new CellRange({
      text: `${startText}:${endText}`,
      start,
      end,
      locks: _.clone(this.locks),
    });
  }

  equals(other: Optional<ASTNode>): boolean {
    return (
      other instanceof RelCellRange &&
      isEqual(this.start, other.start) &&
      isEqual(this.end, other.end) &&
      isEqual(this.locks, other.locks) &&
      this.text === other.text
    );
  }
}

export class Value extends ASTNode {
  eval(env: Environment | null = null): TypedValue {
    return util.astValue(this.typedValue);
  }
  dependencies(env?: Environment): Set<CellIdWithOptionalTable> {
    return new Set();
  }
  constructor(readonly typedValue: TypedValue) {
    super();
  }

  isTerminal(): boolean {
    return true;
  }

  /**
   * This indicates if two values are identical. Only used for tests via `expect(this).equals(other)`,
   * and may behave unexpectedly if two similar values are independently constructed but have denormalized
   * data that may vary slightly.
   */
  equals(other: Optional<ASTNode>): boolean {
    return other instanceof Value && isEqual(this.typedValue, other.typedValue);
  }
}
